/**
 * Sync status slice — "is a sync in flight?" + "when did the last one finish?".
 *
 * Ported from legacy `useStore.ts` (`isSyncing`, `lastSyncAt`, `setSyncing`,
 * `setLastSyncAt`).
 *
 * Pure: the timestamp is passed in by the orchestrator (which reads it from
 * a ClockAdapter) rather than called via `Date.now()` in here — that keeps
 * the reducer deterministic under test (CP3).
 */

export interface SyncStatusState {
  /** True while a sync operation is running. */
  isSyncing: boolean;
  /** Epoch-ms timestamp of the last successful sync, or null if never. */
  lastSyncAt: number | null;
}

export const initialSyncStatusState: SyncStatusState = {
  isSyncing: false,
  lastSyncAt: null,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function setSyncing(
  state: SyncStatusState,
  input: { syncing: boolean }
): SyncStatusState {
  return { ...state, isSyncing: input.syncing };
}

export function setLastSyncAt(
  state: SyncStatusState,
  input: { timestamp: number }
): SyncStatusState {
  return { ...state, lastSyncAt: input.timestamp };
}

/**
 * Convenience: mark a sync as finished. Flips `isSyncing` to false and
 * records the timestamp in a single transition — orchestrators should prefer
 * this over two separate calls so intermediate renders do not briefly show
 * "not syncing but stale timestamp".
 */
export function markSyncFinished(
  state: SyncStatusState,
  input: { timestamp: number }
): SyncStatusState {
  return { ...state, isSyncing: false, lastSyncAt: input.timestamp };
}
